import { t } from '../../i18n';
import {
  CustomerDetail,
  CustomerStatement,
  CustomerStatementEntry,
} from '../../types/customer';
import { formatDate, formatMoney } from '../../utils/format';
import {
  dateCell,
  downloadCustomerExcel,
  escapeHtml,
  excelHeaderRowNumber,
  excelSubtotal,
  htmlDataRow,
  htmlSummaryCard,
  moneyCell,
  printCustomerPdf,
} from './exportCustomerShared';

function sumOutstanding(entries: CustomerStatementEntry[]) {
  let invoice = 0;
  let cash = 0;
  for (const entry of entries) {
    invoice += Number(entry.outstanding.invoice);
    cash += Number(entry.outstanding.cash);
  }
  return {
    invoice: invoice.toFixed(2),
    cash: cash.toFixed(2),
    total: (invoice + cash).toFixed(2),
  };
}

function signedRaw(
  entry: CustomerStatementEntry,
  field: 'invoice' | 'cash',
): string {
  const amount = Number(entry.amounts[field]);
  const signed = entry.direction === 'charge' ? amount : -amount;
  return signed.toFixed(2);
}

function movementLabel(entry: CustomerStatementEntry): string {
  return entry.type === 'INVOICE'
    ? t().customers.invoiceMovement
    : t().customers.paymentMovement;
}

function referenceLabel(entry: CustomerStatementEntry): string {
  if (entry.type === 'INVOICE') return entry.reference;
  return entry.relatedInvoiceNo
    ? `${entry.reference} (${entry.relatedInvoiceNo})`
    : entry.reference;
}

function statementHeaders(): string[] {
  const labels = t().customers;
  return [
    t().common.date,
    labels.movementType,
    labels.reference,
    t().invoices.dueDate,
    t().invoices.daysDue,
    t().financial.invoice,
    t().financial.cash,
    labels.runningInvoice,
    labels.runningCash,
    labels.runningTotal,
  ];
}

function isNumeric(index: number): boolean {
  return index >= 4;
}

export function exportCustomerStatementExcel(
  customer: CustomerDetail,
  statement: CustomerStatement,
  entries: CustomerStatementEntry[],
  openOnly = false,
): void {
  const labels = t().customers;
  const headers = statementHeaders();
  const summaryRows = [
    [
      { type: 'empty' as const },
      { type: 'string' as const, value: t().financial.invoice, style: 'Section' },
      { type: 'string' as const, value: t().financial.cash, style: 'Section' },
      {
        type: 'string' as const,
        value: t().financial.grandTotal,
        style: 'Section',
      },
    ],
    [
      { type: 'string' as const, value: labels.totalCharges, style: 'Label' },
      moneyCell(statement.summary.charges.invoice),
      moneyCell(statement.summary.charges.cash),
      moneyCell(statement.summary.charges.total),
    ],
    [
      { type: 'string' as const, value: labels.totalPayments, style: 'Label' },
      moneyCell(statement.summary.payments.invoice),
      moneyCell(statement.summary.payments.cash),
      moneyCell(statement.summary.payments.total),
    ],
    [
      { type: 'string' as const, value: labels.balance, style: 'Label' },
      moneyCell(statement.summary.outstanding.invoice, 'MoneyBold'),
      moneyCell(statement.summary.outstanding.cash, 'MoneyBold'),
      moneyCell(statement.summary.outstanding.total, 'MoneyBold'),
    ],
  ];
  const headerRow = excelHeaderRowNumber(summaryRows.length);
  const firstDataRow = headerRow + 1;
  const lastDataRow =
    entries.length > 0 ? headerRow + entries.length : headerRow;

  downloadCustomerExcel({
    customer,
    filename: `kartela-pelati-${customer.id}.xls`,
    sheetTitle: openOnly
      ? `${labels.tabs.statement} — ${labels.statementOpenInvoices}`
      : labels.tabs.statement,
    columnWidths: [78, 78, 110, 78, 55, 82, 82, 82, 82, 88],
    columnCount: 10,
    headers,
    isNumericHeader: isNumeric,
    summaryRows,
    dataRows: entries.map((entry) => {
      const daysDue =
        entry.type === 'INVOICE' && entry.daysDue != null && entry.daysDue > 0
          ? entry.daysDue
          : null;
      return [
        dateCell(entry.date),
        { type: 'string' as const, value: movementLabel(entry) },
        { type: 'string' as const, value: referenceLabel(entry) },
        entry.type === 'INVOICE' ? dateCell(entry.dueDate) : { type: 'empty' as const },
        daysDue != null
          ? { type: 'number' as const, value: daysDue, style: 'Int' }
          : { type: 'empty' as const },
        moneyCell(signedRaw(entry, 'invoice')),
        moneyCell(signedRaw(entry, 'cash')),
        moneyCell(
          openOnly ? entry.outstanding.invoice : entry.runningBalance.invoice,
        ),
        moneyCell(
          openOnly ? entry.outstanding.cash : entry.runningBalance.cash,
        ),
        moneyCell(
          openOnly ? entry.outstanding.total : entry.runningBalance.total,
        ),
      ];
    }),
    totalRow: [
      { type: 'string', value: t().common.total, style: 'TotalLabel' },
      { type: 'empty', style: 'TotalEmpty' },
      { type: 'empty', style: 'TotalEmpty' },
      { type: 'empty', style: 'TotalEmpty' },
      { type: 'empty', style: 'TotalEmpty' },
      excelSubtotal(6, firstDataRow, lastDataRow),
      excelSubtotal(7, firstDataRow, lastDataRow),
      moneyCell(
        openOnly
          ? sumOutstanding(entries).invoice
          : statement.summary.outstanding.invoice,
        'MoneyBold',
      ),
      moneyCell(
        openOnly
          ? sumOutstanding(entries).cash
          : statement.summary.outstanding.cash,
        'MoneyBold',
      ),
      moneyCell(
        openOnly
          ? sumOutstanding(entries).total
          : statement.summary.outstanding.total,
        'MoneyBold',
      ),
    ],
  });
}

export function exportCustomerStatementPdf(
  customer: CustomerDetail,
  statement: CustomerStatement,
  entries: CustomerStatementEntry[],
  openOnly = false,
): void {
  const labels = t().customers;
  const headers = statementHeaders();
  const netInvoice = entries.reduce((sum, entry) => {
    return sum + Number(signedRaw(entry, 'invoice'));
  }, 0);
  const netCash = entries.reduce((sum, entry) => {
    return sum + Number(signedRaw(entry, 'cash'));
  }, 0);
  const remaining = openOnly
    ? sumOutstanding(entries)
    : statement.summary.outstanding;

  printCustomerPdf({
    customer,
    title: openOnly
      ? `${labels.tabs.statement} — ${labels.statementOpenInvoices}`
      : labels.tabs.statement,
    summaryHtml: `<div class="summary">${htmlSummaryCard(
      labels.totalCharges,
      statement.summary.charges.invoice,
      statement.summary.charges.cash,
      statement.summary.charges.total,
    )}${htmlSummaryCard(
      labels.totalPayments,
      statement.summary.payments.invoice,
      statement.summary.payments.cash,
      statement.summary.payments.total,
    )}${htmlSummaryCard(
      labels.balance,
      statement.summary.outstanding.invoice,
      statement.summary.outstanding.cash,
      statement.summary.outstanding.total,
    )}</div>`,
    headers,
    isNumeric,
    items: entries,
    rowHtml: (entry) => {
      const daysDue =
        entry.type === 'INVOICE' && entry.daysDue != null && entry.daysDue > 0
          ? String(entry.daysDue)
          : '—';
      return htmlDataRow(
        [
          formatDate(entry.date),
          movementLabel(entry),
          referenceLabel(entry),
          entry.type === 'INVOICE' ? formatDate(entry.dueDate) : '—',
          daysDue,
          formatMoney(signedRaw(entry, 'invoice')),
          formatMoney(signedRaw(entry, 'cash')),
          formatMoney(
            openOnly ? entry.outstanding.invoice : entry.runningBalance.invoice,
          ),
          formatMoney(
            openOnly ? entry.outstanding.cash : entry.runningBalance.cash,
          ),
          formatMoney(
            openOnly ? entry.outstanding.total : entry.runningBalance.total,
          ),
        ],
        isNumeric,
      );
    },
    totalsRowHtml: `
      <tr>
        <td colspan="5">${escapeHtml(t().common.total)}</td>
        <td class="num">${escapeHtml(formatMoney(netInvoice.toFixed(2)))}</td>
        <td class="num">${escapeHtml(formatMoney(netCash.toFixed(2)))}</td>
        <td class="num">${escapeHtml(formatMoney(remaining.invoice))}</td>
        <td class="num">${escapeHtml(formatMoney(remaining.cash))}</td>
        <td class="num">${escapeHtml(formatMoney(remaining.total))}</td>
      </tr>`,
  });
}
